import 'react-phone-number-input/style.css'
import '@xyflow/react/dist/style.css';
import { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ScrollToTop from "./components/Layout/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedLayout from "./components/Layout/ProtectedLayout";
import { UserProvider } from './context/UserContext';

// Dashboard
import Home from "./pages/dashboard/Home";
import SplashScreen from "./pages/SplashScreen";

// Authentication
import SignIn from "./pages/authentication/SignIn";
import SignUp from "./pages/authentication/SignUp";
import ForgotPassword from "./pages/authentication/ForgotPassword";
import ResetPassword from "./pages/authentication/ResetPassword";
import ConfirmEmail from "./pages/authentication/ConfirmEmail";
import Logout from "./pages/authentication/Logout";

import NotFound from "./pages/NotFound";

// Settings
import { AuthProvider } from "./context/AuthContext";
import AddressSettings from './pages/settings/AddressSettings';
import AccountSettings from "./pages/settings/AccountSettings";
import ChangePassword from "./pages/settings/ChangePassword";
import Connections from "./pages/settings/Connections";
import PrivacyPolicy from "./pages/settings/PrivacyPolicy";
import TermsConditions from "./pages/settings/TermsConditions";
import SecuritySettingsPage from "./pages/settings/SecuritySettings";

import Orders from './pages/order/List';
import OrderDetails from './pages/order/Details';
import Team from './pages/team/Team';
import BonosInfluencer from './pages/bonus/BonusInfluencer';
import BonosRegalias from './pages/bonus/BonusRegalias';
import Rank from './pages/rank/Rank';
import Cart from './pages/ecommerce/Cart';
import ProductsDetails from './pages/ecommerce/ProductsDetails';
import DocumentsSettings from './pages/settings/DocumentSettings';
import AccountBankSettings from './pages/settings/AccountBankSettings';


// Create a client
const queryClient = new QueryClient();

const AppContent = () => {
  const [isSidebarActive, setSidebarActive] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;

  const toggleActive = (): void => {
    setSidebarActive(!isSidebarActive);
  };

  const isAuthPage = [
    "/",
    "/login",
    "/splash",
    "/sign-up",
    "/forgot-password",
    "/reset-password",
    "/confirm-email",
    "/logout",
    "/not-found",
  ].includes(pathname);

  return (
    <>
      <ScrollToTop />
      <div className={`main-wrapper-content ${isSidebarActive ? "active" : ""}`}>
        <div className={isAuthPage && pathname !== "/" ? "main-content d-flex flex-column" : "d-flex flex-column"}>
          <Routes>
            <Route path="/" element={<SplashScreen />} />
            <Route path="/login" element={<SignIn />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/not-found" element={<NotFound />} />

            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <UserProvider>
                    <ProtectedLayout toggleActive={toggleActive} isSidebarActive={isSidebarActive}>
                      <Routes>
                        {/* Dashboard */}
                        <Route path="store" element={<Home />} />
                        <Route path="store/product-details/:id" element={<ProductsDetails />} />
                        <Route path="store/cart" element={<Cart />} />
                        <Route path="orders" element={<Orders />} />
                        <Route path="order/details/:id" element={<OrderDetails />} />
                        <Route path="team" element={<Team />} />

                        <Route path="influencer-bonus" element={<BonosInfluencer />} />
                        <Route path="override-bonus" element={<BonosRegalias />} />
                        <Route path="rank" element={<Rank />} />

                        {/* Settings */}
                        <Route path="/my-profile" element={<AccountSettings />} />
                        <Route path="settings/change-password" element={<ChangePassword />} />
                        <Route path="settings/security" element={<SecuritySettingsPage />} />
                        <Route path="settings/connections" element={<Connections />} />
                        <Route path="settings/address" element={<AddressSettings />} />
                        <Route path="settings/documents" element={<DocumentsSettings />} />
                        <Route path="settings/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="settings/terms-conditions" element={<TermsConditions />} />
                        <Route path="settings/account-bank" element={<AccountBankSettings />} />

                        {/* Fallback */}
                        <Route path="*" element={<Navigate to="/not-found" replace />} />
                      </Routes>
                    </ProtectedLayout>
                  </UserProvider>
                </ProtectedRoute>
              }
            />

          </Routes>
        </div>
      </div>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
